// =====================================================
// USE PRICING RULES HOOK
// Stage 2: Participants & Pricing
// Hook for managing pricing rules
// =====================================================

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { pricingRulesRepository } from '@/data/pricingRulesRepository';
import type {
  CreatePricingRuleData,
  UpdatePricingRuleData,
} from '@/types/pricing.types';

// ===================
// QUERY KEYS
// ===================

export const pricingRulesKeys = {
  all: ['pricingRules'] as const,
  byEvent: (eventId: string) => ['pricingRules', 'event', eventId] as const,
  detail: (id: string) => ['pricingRules', 'detail', id] as const,
  statistics: (eventId: string) => ['pricingRules', 'statistics', eventId] as const,
};

// ===================
// HOOKS
// ===================

/**
 * Hook to fetch pricing rules for an event
 */
export function usePricingRules(eventId: string) {
  const [filters, setFilters] = useState<{ is_active?: boolean; search?: string }>({});

  const { data: rules = [], isLoading, error } = useQuery({
    queryKey: [...pricingRulesKeys.byEvent(eventId), filters],
    queryFn: () => pricingRulesRepository.getPricingRulesByEvent(eventId, filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    rules,
    isLoading,
    error,
    filters,
    setFilters,
  };
}

/**
 * Hook to fetch a single pricing rule
 */
export function usePricingRule(id: string | null) {
  const { data: rule, isLoading, error } = useQuery({
    queryKey: pricingRulesKeys.detail(id || ''),
    queryFn: () => (id ? pricingRulesRepository.getPricingRuleById(id) : null),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });

  return {
    rule,
    isLoading,
    error,
  };
}

/**
 * Hook for pricing rule mutations
 */
export function usePricingRuleMutations(eventId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: pricingRulesKeys.byEvent(eventId) });
    queryClient.invalidateQueries({ queryKey: pricingRulesKeys.statistics(eventId) });
  }, [queryClient, eventId]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreatePricingRuleData) => pricingRulesRepository.createPricingRule(data),
    onSuccess: () => {
      invalidateQueries();
      toast({
        title: 'Regla creada',
        description: 'La regla de precios se ha creado exitosamente',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error al crear regla',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePricingRuleData }) =>
      pricingRulesRepository.updatePricingRule(id, data),
    onSuccess: () => {
      invalidateQueries();
      toast({
        title: 'Regla actualizada',
        description: 'La regla de precios se ha actualizado exitosamente',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error al actualizar regla',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => pricingRulesRepository.deletePricingRule(id),
    onSuccess: () => {
      invalidateQueries();
      toast({
        title: 'Regla eliminada',
        description: 'La regla de precios se ha eliminado exitosamente',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error al eliminar regla',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Toggle status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      pricingRulesRepository.togglePricingRuleStatus(id, isActive),
    onSuccess: (_data, variables) => {
      invalidateQueries();
      toast({
        title: variables.isActive ? 'Regla activada' : 'Regla desactivada',
        description: `La regla ha sido ${variables.isActive ? 'activada' : 'desactivada'}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error al cambiar estado',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Move up mutation
  const moveUpMutation = useMutation({
    mutationFn: (ruleId: string) => pricingRulesRepository.movePricingRuleUp(eventId, ruleId),
    onSuccess: () => {
      invalidateQueries();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error al mover regla',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Move down mutation
  const moveDownMutation = useMutation({
    mutationFn: (ruleId: string) => pricingRulesRepository.movePricingRuleDown(eventId, ruleId),
    onSuccess: () => {
      invalidateQueries();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error al mover regla',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Duplicate mutation
  const duplicateMutation = useMutation({
    mutationFn: (id: string) => pricingRulesRepository.duplicatePricingRule(id),
    onSuccess: () => {
      invalidateQueries();
      toast({
        title: 'Regla duplicada',
        description: 'La regla se ha duplicado exitosamente',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error al duplicar regla',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Reorder mutation
  const reorderMutation = useMutation({
    mutationFn: (ruleIdsInOrder: string[]) =>
      pricingRulesRepository.reorderPricingRules(eventId, ruleIdsInOrder),
    onSuccess: () => {
      invalidateQueries();
      toast({
        title: 'Orden actualizado',
        description: 'El orden de las reglas se ha actualizado',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error al reordenar',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    createPricingRule: createMutation.mutateAsync,
    updatePricingRule: (id: string, data: UpdatePricingRuleData) =>
      updateMutation.mutateAsync({ id, data }),
    deletePricingRule: deleteMutation.mutateAsync,
    togglePricingRuleStatus: (id: string, isActive: boolean) =>
      toggleStatusMutation.mutateAsync({ id, isActive }),
    movePricingRuleUp: moveUpMutation.mutateAsync,
    movePricingRuleDown: moveDownMutation.mutateAsync,
    duplicatePricingRule: duplicateMutation.mutateAsync,
    reorderPricingRules: reorderMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}

/**
 * Hook to fetch pricing rule statistics
 */
export function usePricingRuleStatistics(eventId: string) {
  const { data: statistics, isLoading, error } = useQuery({
    queryKey: pricingRulesKeys.statistics(eventId),
    queryFn: () => pricingRulesRepository.getPricingRuleStatistics(eventId),
    staleTime: 5 * 60 * 1000,
  });

  return {
    statistics: statistics || [],
    isLoading,
    error,
  };
}
